import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import en from 'src/locales/en.json';
import es from 'src/locales/es.json';
import fr from 'src/locales/fr.json';
import de from 'src/locales/de.json';
import zh_CN from 'src/locales/zh_CN.json';
import it from 'src/locales/it.json';
import ja from 'src/locales/ja.json';
import ko from 'src/locales/ko.json';
import pt from 'src/locales/pt.json';
import nl from 'src/locales/nl.json';
import ar from 'src/locales/ar.json';
import cs from 'src/locales/cs.json';
import da from 'src/locales/da.json';
import el from 'src/locales/el.json';
import fi from 'src/locales/fi.json';
import he from 'src/locales/he.json';
import hu from 'src/locales/hu.json';
import id from 'src/locales/id.json';
import nb from 'src/locales/nb.json';
import pl from 'src/locales/pl.json';
import ro from 'src/locales/ro.json';
import sv from 'src/locales/sv.json';
import sw from 'src/locales/sw.json';
import th from 'src/locales/th.json';
import tr from 'src/locales/tr.json';
import vi from 'src/locales/vi.json';
import zh_TW from 'src/locales/zh_TW.json';
import en_AU from 'src/locales/en_AU.json';
import en_GB from 'src/locales/en_GB.json';
import es_ES from 'src/locales/es_ES.json';
import fr_CA from 'src/locales/fr_CA.json';
import pt_PT from 'src/locales/pt_PT.json';
import ru from 'src/locales/ru.json';
import bg from 'src/locales/bg.json';
import hr from 'src/locales/hr.json';
import sk from 'src/locales/sk.json';
import lt from 'src/locales/lt.json';
import hi from 'src/locales/hi.json';
import fa from 'src/locales/fa.json';
import is from 'src/locales/is.json';
import tl from 'src/locales/tl.json';
import ms from 'src/locales/ms.json';
import uk from 'src/locales/uk.json';
import af from 'src/locales/af.json';
import my from 'src/locales/my.json';
import zu from 'src/locales/zu.json';
import xh from 'src/locales/xh.json';
import yo from 'src/locales/yo.json';
import ig from 'src/locales/ig.json';
import ha from 'src/locales/ha.json';

const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  de: { translation: de },
  zh: { translation: zh_CN }, // Map zh -> zh_CN for automatic fallback
  zh_CN: { translation: zh_CN },
  it: { translation: it },
  ja: { translation: ja },
  ko: { translation: ko },
  pt: { translation: pt },
  nl: { translation: nl },
  ar: { translation: ar },
  cs: { translation: cs },
  da: { translation: da },
  el: { translation: el },
  fi: { translation: fi },
  he: { translation: he },
  hu: { translation: hu },
  id: { translation: id },
  nb: { translation: nb },
  pl: { translation: pl },
  ro: { translation: ro },
  sv: { translation: sv },
  sw: { translation: sw },
  th: { translation: th },
  tr: { translation: tr },
  vi: { translation: vi },
  zh_TW: { translation: zh_TW },
  en_AU: { translation: en_AU },
  en_GB: { translation: en_GB },
  es_ES: { translation: es_ES },
  fr_CA: { translation: fr_CA },
  pt_PT: { translation: pt_PT },
  ru: { translation: ru },
  bg: { translation: bg },
  hr: { translation: hr },
  sk: { translation: sk },
  lt: { translation: lt },
  hi: { translation: hi },
  fa: { translation: fa },
  'fa-IR': { translation: fa }, // Persian - Iran fallback
  'fa-AF': { translation: fa }, // Persian - Afghanistan fallback
  is: { translation: is },
  tl: { translation: tl },
  ms: { translation: ms },
  uk: { translation: uk },
  af: { translation: af },
  my: { translation: my },
  zu: { translation: zu },
  xh: { translation: xh },
  yo: { translation: yo },
  ig: { translation: ig },
  ha: { translation: ha },
};

// Generate automatic fallbacks: if region variant doesn't exist, fall back to base language
const generateFallbackLng = () => {
  const availableLanguages = Object.keys(resources);
  const fallbacks: Record<string, string[]> = { default: ['en'] };
  
  availableLanguages.forEach(lang => {
    if (lang.includes('_') || lang.includes('-')) {
      // Regional variant exists, set its fallback to base language then English
      const baseLang = lang.split(/[_-]/)[0];
      fallbacks[lang] = [lang, baseLang, 'en'].filter(l => availableLanguages.includes(l));
    } else {
      // Base language, fallback to English
      fallbacks[lang] = [lang, 'en'];
    }
  });
  
  return fallbacks;
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    debug: process.env.NODE_ENV === 'development',
    
    // Automatic language fallbacks for all regional variants
    fallbackLng: generateFallbackLng(),
    
    // Language detection configuration
    detection: {
      order: ['navigator', 'htmlTag'],
      caches: [],
      excludeCacheFor: ['cimode'], // never cache if language = 'cimode'
    },
    
    interpolation: {
      escapeValue: false, // React already does escaping
    },
    
    // Use single namespace
    defaultNS: 'translation',
    
    // Key separator and nesting
    keySeparator: '.',
    nsSeparator: ':',
  });

export default i18n;